import { FC, Fragment, memo, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { hasSuccessBind, logout } from '@/apis/auth';
import { clearAuthCookies, getAgentId } from '@/utils';
import { agentsAtom } from '@/stores/agent';
import im from '@/utils/im';
import cx from 'clsx';
import { BASE_URL } from '@/constants';
import AgentAvatar from '@/components/agent-avatar';
import { Separator } from '@/components/ui/separator';
import {
  DownIcon,
  SignOutIcon,
  MailIcon,
  UpIcon,
  EditIcon,
  MarketingIcon,
  ColumnIcon,
  LabelIcon,
  HomeIcon,
  LightningIcon,
  SettingIcon,
  RemindIcon,
  BookTwoIcon,
} from '@wisdesign/lsicon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import useCustomerEventListener from '@/hooks/use-customer-event-listener';
import { AgentInfo } from '@/interfaces/agent';
import { LocalSocketStatusActions } from '@/interfaces/event';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { SideMenu, SideMenuItem } from '@/components/side-menu';
import { useRequest } from 'ahooks';
import { NoPermissionError } from '@/utils/request/errors';
import Loading from '@/components/loading';
import UpdatePasswordSheet from '@/pages/setting/setting/component/update-password';
import { lazy, Suspense } from 'react';
import useModal from '@/hooks/use-modal';
const GlobalSearchRenderer = lazy(() =>
  import('@/components/global-search').then((m) => ({ default: m.GlobalSearchRenderer })),
);
import { tagsAtom } from '@/stores/tags';
import { accountsAtom } from '@/stores/channel';
import StatusSwitcher from '@/components/ui/status-switcher';
import { updateAgent } from '@/apis/agent';
import { errorHandler } from '@/utils/request';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { resetUser } from '@/utils/mixpanel';
import {
  entAccountAtom,
  entAccountAvailablePointsAtom,
  entAccountDataAtom,
  entPackageDayLeftAtom,
} from '@/stores/ent-account';
import { agentPermissionsAtom } from '@/stores/role';
const PointCommonDialog = lazy(
  () => import('@/pages/paid-management/componets/point-common-dialog'),
);
import { usePointDialog } from '@/hooks/use-point-dialog';
import EntBalanceBanner from '@/components/ent-balance-banner';
import {
  getPointDaysLeft,
  getPointWarningDialogType,
  getPointWarningCookieName,
  clearAllPointWarningCookies,
  getPackageExpirationDialogType,
  getPackageExpirationCookieName,
  clearAllPackageExpirationCookies,
} from '@/utils/paid-management';
import Cookies from 'js-cookie';
const PointWarningDialog = lazy(() => import('@/components/point-warning-dialog'));
import { usePointWarningDialog } from '@/hooks/use-point-warning-dialog';
const PackageExpirationDialog = lazy(() => import('@/components/package-expiration-dialog'));
import { usePackageExpirationDialog } from '@/hooks/use-package-expiration-dialog';
import { usePermissions } from '@/hooks/use-permissions';
const ConversationRemindSheet = lazy(() => import('@/components/conversation-remind-sheet'));
import { refreshNotifySettingAtom } from '@/stores/notify-setting';
import { quickReplyAtom } from '@/stores/quick-reply';

interface MenuItemConfig {
  key: string;
  icon: ReactNode;
  label: string;
  path: string;
  hide?: boolean;
  permission?: string; // 新增权限字段
}

const MainLayout = memo(() => {
  const [alertConnectFailed, setAlertConntectFailed] = useState(false);
  const [fold, setFold] = useState(() => {
    const savedFold = localStorage.getItem('sidebar-fold');
    return savedFold ? JSON.parse(savedFold) : false;
  });
  const [infoOpen, setInfoOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [{ agentsMap }, refreshAgents] = useAtom(agentsAtom);
  const refreshTags = useSetAtom(tagsAtom);
  const refreshAccounts = useSetAtom(accountsAtom);
  const refreshEntAccount = useSetAtom(entAccountAtom);
  const refreshAgentPermissions = useSetAtom(agentPermissionsAtom);
  const refreshQuickReply = useSetAtom(quickReplyAtom);
  const { openPointCommonDialog, setOpenPointCommonDialog } = usePointDialog();
  const { open, type, backToHome, openDialog, closeDialog } = usePointWarningDialog();
  const {
    open: packageExpirationOpen,
    type: packageExpirationType,
    openDialog: openPackageExpirationDialog,
    closeDialog: closePackageExpirationDialog,
  } = usePackageExpirationDialog();
  const availablePoints = useAtomValue(entAccountAvailablePointsAtom);
  const entAccount = useAtomValue(entAccountDataAtom);
  const averageDailyConsumePoints = entAccount?.averageDailyConsumePoints ?? 0;
  const daysLeft = getPointDaysLeft(availablePoints, averageDailyConsumePoints);
  const packageDaysLeft = useAtomValue(entPackageDayLeftAtom);

  const prevAvailablePointsRef = useRef<number | undefined>(undefined);
  const prevPackageExpiredAtRef = useRef<number | undefined | null>(undefined);

  const [
    { visible: updatePasswordOpen },
    { open: openUpdatePassword, close: closeUpdatePassword },
  ] = useModal();

  const [
    { visible: conversationRemindOpen },
    { open: openConversationRemind, close: closeConversationRemind },
  ] = useModal();

  const refreshNotifySetting = useSetAtom(refreshNotifySettingAtom);

  const agent = agentsMap[getAgentId()];

  const selectedKey = useMemo(() => `/${pathname.split('/')[1]}`, [pathname]);

  // 使用权限hook
  const { hasPermission } = usePermissions();

  // 根据权限动态生成菜单配置
  const menus = useMemo(() => {
    const baseMenus: Array<{ key: string; children: MenuItemConfig[] }> = [
      {
        key: 'main',
        children: [
          { key: '/home', icon: <HomeIcon />, label: '首页', path: '/home' },
          {
            key: '/chat',
            icon: <MailIcon />,
            label: '私信',
            path: '/chat',
            permission: 'im:viewPage',
            hide: !hasPermission('im:viewPage'),
          },
          {
            key: '/channel',
            icon: <MarketingIcon />,
            label: '渠道账号',
            path: '/channel',
            permission: 'channelAccount:viewPage',
            hide: !hasPermission('channelAccount:viewPage'),
          },
        ],
      },
      {
        key: 'setting',
        children: [
          {
            key: '/bi',
            icon: <ColumnIcon />,
            label: '统计',
            path: '/bi',
            permission: 'statistics:viewPage',
            hide: !hasPermission('statistics:viewPage'),
          },
          {
            key: '/knowledge',
            icon: <BookTwoIcon />,
            label: '知识空间',
            path: '/knowledge',
            // permission: 'knowledge:viewPage',
            // hide: !hasPermission('knowledge:viewPage'),
          },
          {
            key: '/tag',
            icon: <LabelIcon />,
            label: '标签管理',
            path: '/tag',
            permission: 'tag:viewPage',
            hide: !hasPermission('tag:viewPage'),
          },
          {
            key: '/automation',
            icon: <LightningIcon />,
            label: '自动化',
            path: '/automation/reception',
            permission: 'automation:viewPage',
            hide: !hasPermission('automation:viewPage'),
          },
          {
            key: '/setting',
            icon: <SettingIcon />,
            label: '设置',
            path: '/setting',
            permission: 'settings:viewPage',
            hide: !hasPermission('settings:viewPage'),
          },
        ],
      },
    ];

    // 过滤掉隐藏的菜单项
    return baseMenus
      .map((group) => ({
        ...group,
        children: group.children.filter((item) => !item.hide),
      }))
      .filter((group) => group.children.length > 0);
  }, [hasPermission]);

  useEffect(() => {
    refreshAgentPermissions();
    refreshAgents();
    refreshTags();
    refreshAccounts();
    refreshEntAccount();
    refreshNotifySetting();
    refreshQuickReply();

    const timer = setInterval(() => {
      refreshEntAccount();
    }, 60000);

    const onConnectFailed = () => setAlertConntectFailed(true);
    const onConnectSucceed = () => setAlertConntectFailed(false);

    im.connect();
    im.on(LocalSocketStatusActions.CONNECTED, onConnectSucceed);
    im.on(LocalSocketStatusActions.DISCONNECTED, onConnectFailed);
    im.on('ent_points_used_up', refreshEntAccount);
    return () => {
      clearInterval(timer);
      im.off(LocalSocketStatusActions.CONNECTED, onConnectSucceed);
      im.off(LocalSocketStatusActions.DISCONNECTED, onConnectFailed);
      im.off('ent_points_used_up', refreshEntAccount);
      im.disconnect();
    };
  }, []);

  useEffect(() => {
    if (
      entAccount &&
      prevAvailablePointsRef.current &&
      availablePoints > prevAvailablePointsRef.current
    ) {
      clearAllPointWarningCookies();
    }
    prevAvailablePointsRef.current = availablePoints;
  }, [availablePoints, entAccount]);

  useEffect(() => {
    if (
      entAccount &&
      prevPackageExpiredAtRef.current !== undefined &&
      prevPackageExpiredAtRef.current !== null &&
      entAccount.packageExpiredAt &&
      entAccount.packageExpiredAt > prevPackageExpiredAtRef.current
    ) {
      clearAllPackageExpirationCookies();
    }
    prevPackageExpiredAtRef.current = entAccount?.packageExpiredAt;
  }, [entAccount]);

  // 检查并显示套餐到期弹窗
  useEffect(() => {
    if (!entAccount) {
      return;
    }

    // 获取当前应该显示的弹窗类型（优先级：已到期 > 1天到期 > 3天到期）
    const actualType = getPackageExpirationDialogType(packageDaysLeft, entAccount.packageStatus);

    if (actualType && !Cookies.get(getPackageExpirationCookieName(actualType))) {
      openPackageExpirationDialog(actualType);
      Cookies.set(getPackageExpirationCookieName(actualType), '1');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageDaysLeft, entAccount]);

  useEffect(() => {
    if (!entAccount) {
      return;
    }
    if (!entAccount?.isAvailable) {
      const restrictedRoutes = ['/automation', '/tag'];
      const isRestrictedRoute = restrictedRoutes.some((route) => pathname.startsWith(route));
      const isAiAgentRoute = pathname.includes('/ai-agent');
      if (isRestrictedRoute || isAiAgentRoute) {
        openDialog('insufficient', true);
      }
      return;
    }

    const warningType = getPointWarningDialogType(daysLeft);
    if (warningType && !Cookies.get(getPointWarningCookieName(warningType))) {
      openDialog(warningType);
      Cookies.set(getPointWarningCookieName(warningType), '1');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePoints, daysLeft, entAccount, pathname]);

  // 全局监听事件
  useCustomerEventListener();

  const onLogout = () => {
    logout();
    im.disconnect();
    window.location.href = '/login';
    clearAuthCookies();
    resetUser();
  };

  const onReconnect = () => {
    im.connect();
    setAlertConntectFailed(false);
  };

  const handleStatusChange = (value: number) => {
    updateAgent({ id: agent.id, status: value })
      .then(() => {
        refreshAgents();
      })
      .catch(errorHandler);
  };

  return (
    <div className="flex h-full bg-shell overflow-x-auto">
      <div
        className={cx(
          'h-full flex flex-col flex-shrink-0 gap p-lg',
          fold ? 'w-[80px]' : 'w-[195px]',
        )}
      >
        <div
          className={cx(
            'my cursor-pointer h-[36px] bg-contain bg-no-repeat transition-none select-none',
            fold ? 'bg-center' : '',
          )}
          style={{ backgroundImage: `url(${BASE_URL}/${fold ? 'logo-mini' : 'logo'}.png)` }}
          onClick={() => {
            const newFold = !fold;
            setFold(newFold);
            localStorage.setItem('sidebar-fold', JSON.stringify(newFold));
          }}
        />

        <div className="flex-1 w-full flex flex-col gap-sm space-y">
          {menus.map((group, i) => (
            <Fragment key={group.key}>
              <SideMenu>
                {group.children.map(({ key, icon, label, path }) => (
                  <SideMenuItem
                    key={key}
                    selected={selectedKey === key}
                    onClick={() => navigate(path)}
                    className={cx('h-[20px] box-content', fold ? 'justify-center' : '')}
                  >
                    {fold ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="w-full flex justify-center">{icon}</div>
                        </TooltipTrigger>
                        <TooltipContent>{label}</TooltipContent>
                      </Tooltip>
                    ) : (
                      <>
                        {icon}
                        {label}
                      </>
                    )}
                  </SideMenuItem>
                ))}
              </SideMenu>
              {i < menus.length - 1 && <Separator className="bg-black/10" />}
            </Fragment>
          ))}
        </div>

        <EntBalanceBanner fold={fold} />

        <DropdownMenu open={infoOpen} onOpenChange={setInfoOpen}>
          <DropdownMenuContent className="w-[162px]" align="start">
            <div className="flex py gap text-sm">
              <AgentAvatar agent={agent} size={32} />
              <div className="flex-1 text-left flex flex-col">
                <span className="font-medium">{agent?.name}</span>
                <span className="text-menu">{agent?.telephone}</span>
              </div>
            </div>
            <Separator className="my" />
            <StatusSwitcher value={agent?.status} onChange={handleStatusChange} />
            <Separator className="my" />
            <DropdownMenuItem onClick={openConversationRemind}>
              <RemindIcon />
              对话提醒
            </DropdownMenuItem>
            <DropdownMenuItem onClick={openUpdatePassword}>
              <EditIcon />
              修改密码
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onLogout}>
              <SignOutIcon />
              退出登录
            </DropdownMenuItem>
          </DropdownMenuContent>
          <InfoTrigger open={infoOpen} agent={agent} fold={fold} />
        </DropdownMenu>
      </div>
      {alertConnectFailed && (
        <Alert className="fixed right-1 top-1 shadow-2xl z-50 w-[480px]">
          <AlertTitle>提示</AlertTitle>
          <AlertDescription className="flex justify-between items-center gap-md">
            当前网络不稳定，请检查当前网络状态后，点击「重连」进行重试
            <Button variant="primary" onClick={onReconnect}>
              重连
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <div className="flex-1 overflow-auto border border-page rounded-xl min-w-[1000px] my mr shadow-module-card bg-page">
        <Outlet />
      </div>
      <Suspense fallback={<Loading className="h-screen" tip="加载中..." />}>
        <UpdatePasswordSheet
          open={updatePasswordOpen}
          onOk={closeUpdatePassword}
          onClose={closeUpdatePassword}
        />
      </Suspense>
      <Suspense fallback={<Loading className="h-screen" tip="加载中..." />}>
        <GlobalSearchRenderer />
      </Suspense>
      <Suspense fallback={<Loading className="h-screen" tip="加载中..." />}>
        <PointCommonDialog
          open={openPointCommonDialog}
          onOpenChange={(open) => {
            setOpenPointCommonDialog(open);
          }}
        />
      </Suspense>
      <Suspense fallback={<Loading className="h-screen" tip="加载中..." />}>
        <PointWarningDialog open={open} type={type} backToHome={backToHome} onClose={closeDialog} />
      </Suspense>
      <Suspense fallback={<Loading className="h-screen" tip="加载中..." />}>
        <ConversationRemindSheet
          open={conversationRemindOpen}
          onOpenChange={closeConversationRemind}
        />
      </Suspense>
      <Suspense fallback={<Loading className="h-screen" tip="加载中..." />}>
        <PackageExpirationDialog
          open={packageExpirationOpen}
          type={packageExpirationType}
          onClose={closePackageExpirationDialog}
        />
      </Suspense>
    </div>
  );
});
MainLayout.displayName = 'MainLayout';

const Wrapper = () => {
  const { loading, data, error } = useRequest(hasSuccessBind);
  if (loading) {
    return <Loading className="h-screen" tip="页面加载中..." />;
  }
  if (error instanceof NoPermissionError) {
    return;
  }
  if (!data && !window.location.pathname.includes('/channel-auth') && !error) {
    window.location.href = '/channel-auth';
    return;
  }

  return <MainLayout />;
};
export default memo(Wrapper);

interface InfoTriggerProps {
  open: boolean;
  agent: AgentInfo;
  fold: boolean;
}
const InfoTrigger: FC<InfoTriggerProps> = ({ open, agent, fold }) => {
  return (
    <DropdownMenuTrigger
      className={cx(
        'outline-none w-full flex items-center gap p-[7px] text-sm rounded-md cursor-pointer border',
        open ? 'bg-active' : 'bg-box',
      )}
    >
      <AgentAvatar agent={agent} />
      {!fold && (
        <>
          <div className="flex-1 text-left flex flex-col">
            <span className="font-medium">{agent?.name}</span>
            <span className="text-menu leading-4">{agent?.telephone}</span>
          </div>
          {open ? <UpIcon /> : <DownIcon />}
        </>
      )}
    </DropdownMenuTrigger>
  );
};
